import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Checkbox,
  Tooltip, // Import Tooltip
} from "@mui/material";
import { styled } from "@mui/system";
import { Box } from "@material-ui/core";
import { data_example } from "../../Pages/SeatingPlansPage/data_example";
import { getRowLayout, ROW_NUMBER_INDEX } from "../../Helpers/seat_layout";

const CustomCheckbox = styled(Checkbox)({
  "& .MuiSvgIcon-root": {
    fill: "transparent",
    stroke: "white",
  },
  "&.Mui-checked": {
    "& .MuiSvgIcon-root": {
      fill: "white",
      stroke: "white",
    },
  },
});

const StyledTableCell = styled(TableCell)({
  padding: "0 5px",
  border: "none",
});

interface SeatingPlanTableProps {
  seatColor: string;
  price: number;
  selectedCells: any;
  setSelectedCells: React.Dispatch<React.SetStateAction<any>>;
  appliedCells: any;
}

function addMissingNumbers(arr: string[]): string[] {
  const numArr: number[] = arr.map(Number);
  const min: number = Math.min(...numArr);
  const max: number = Math.max(...numArr);
  const currentNumbers: Set<number> = new Set(numArr);
  const completeRange: number[] = Array.from(
    { length: max - min + 1 },
    (_, i) => i + min
  );
  const missingNumbers: number[] = completeRange.filter(
    (num) => !currentNumbers.has(num)
  );
  const missingNumbersAsString: string[] = missingNumbers.map(String);
  return [...arr, ...missingNumbersAsString].sort((a, b) => Number(a) - Number(b));
}

const SeatingPlanTableNew: React.FC<SeatingPlanTableProps> = ({
  seatColor,
  price,
  selectedCells,
  setSelectedCells,
  appliedCells,
}) => {
  const handleCellClick = (rowIndex: string, colIndex: string) => {
    const tempObj = { ...selectedCells };
    if (!tempObj[rowIndex]) {
      tempObj[rowIndex] = {};
    }
    if (!tempObj?.[rowIndex]?.[colIndex]) {
      tempObj[rowIndex][colIndex] = {
        price,
        status: "open",
        ordered: "no",
        category: seatColor,
      };
    } else {
      delete tempObj[rowIndex][colIndex];
      if (Object.keys(tempObj[rowIndex]).length === 0) {
        delete tempObj[rowIndex];
      }
    }

    setSelectedCells(tempObj);
  };

  const handleRowCheckboxChange = (rowIndex: string) => {
    setSelectedCells((prevSelectedCells: any) => {
      const newSelectedCells = { ...prevSelectedCells };
      const currentColumns =
        appliedCells[rowIndex] || data_example[rowIndex as keyof typeof data_example] || {};

      if (
        newSelectedCells[rowIndex] &&
        Object.keys(newSelectedCells[rowIndex]).length ===
          Object.keys(currentColumns).length
      ) {
        delete newSelectedCells[rowIndex];
      } else {
        newSelectedCells[rowIndex] = {};
        Object.keys(currentColumns).forEach((colIndex) => {
          newSelectedCells[rowIndex][colIndex] = {
            price,
            status: "open",
            ordered: "no",
            category: seatColor,
          };
        });
      }

      return newSelectedCells;
    });
  };

  const arrayOfLength19 = Array.from({ length: 19 }, () => 0);
  // Generate 15 rows instead of using existing data keys
  const rowsIndexArr = Array.from({ length: 15 }, (_, i) => (i + 1).toString());
  return (
    <div
      style={{
        padding: "10px",
        width: "80%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Paper
        style={{
          width: "70%",
          maxWidth: "800px",
          backgroundColor: "#07231D",
          padding: "30px",
          borderRadius: "10px"
        }}
      >
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "30px",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
            marginLeft: "60px",
          }}
        >
          <svg width="604" height="62" viewBox="0 0 604 62" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M598 6C482.821 68.024 144 77.1532 6 6.00008" stroke="white" stroke-width="12" stroke-linecap="round"/>
</svg>
          <Box
            style={{
              fontSize: "20px",
              fontWeight: "700",
              color: "white",
              position: "absolute",
              bottom: "25px",
            }}
          >
            Stage
          </Box>
        </Box>
        <Table size="small">
          <TableBody>
            {rowsIndexArr?.map((row_index) => {
              const currentColumns = appliedCells[row_index as keyof typeof appliedCells] || {};
              const seatCount = Object.keys(currentColumns).length;
              const selectedCount = Object.keys(selectedCells[row_index] || {}).length;

              return (
                <React.Fragment key={`row-${row_index}`}>
                  <TableRow style={{ height: "30px" }}>
                    <StyledTableCell padding="checkbox">
                      <CustomCheckbox
                        size="small"
                        checked={seatCount > 0 && selectedCount === seatCount}
                        onChange={() => handleRowCheckboxChange(row_index)}
                      />
                    </StyledTableCell>

                    {/* Seats laid out from the plan JSON (see getRowLayout), row number in the middle */}
                    {getRowLayout(currentColumns).map((column_index, index) => {
                      const cellData = column_index !== null
                        ? (selectedCells[row_index] && selectedCells[row_index][column_index]) || currentColumns[column_index]
                        : undefined;
                      const cellPrice = cellData?.price;

                      return (
                        <React.Fragment key={`row-${row_index}-pos-${index}`}>
                          {index === ROW_NUMBER_INDEX && (
                            <TableCell
                              style={{
                                backgroundColor: "transparent",
                                padding: 0,
                                textAlign: "center",
                                border: "none",
                                width: "20px",
                                position: "relative",
                                color: "#ffffff",
                              }}
                            >
                              {row_index}
                            </TableCell>
                          )}
                          {column_index === null ? (
                            // Gap between the two blocks of a shorter row
                            <TableCell
                              style={{
                                backgroundColor: "transparent",
                                padding: 0,
                                border: "none",
                                width: "20px",
                              }}
                            />
                          ) : (
                            <TableCell
                              style={{
                                padding: 0,
                                textAlign: "center",
                                border: "none",
                                cursor: "pointer",
                                width: "20px",
                                position: "relative",
                                color: "#ffffff",
                              }}
                            >
                              <Tooltip
                                title={
                                  <Box sx={{display:"flex",flexDirection:"column"}}>
                                    <img src={cellData?.setView} width={200}/>
                                    <p>
                                      {`Price: ${
                                          cellPrice !== null && cellPrice !== undefined
                                              ? cellPrice
                                              : "Not available"
                                      }`}
                                    </p>
                                    <p>
                                      {`CellData: ${
                                          row_index} + ${
                                            column_index}`}
                                    </p>
                                  </Box>
                                }
                              >
                                <div
                                  onClick={() => {
                                    handleCellClick(row_index, column_index);
                                  }}
                                  style={{
                                    backgroundColor:
                                      selectedCells[row_index] &&
                                      selectedCells[row_index][column_index]
                                        ? selectedCells[row_index][column_index].category
                                        : cellData?.category || "white",
                                    textAlign: "center",
                                    fontSize: "8px",
                                    margin: "auto",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "18px",
                                    height: "18px",
                                    borderTopLeftRadius: "5px",
                                    borderTopRightRadius: "5px",
                                  }}
                                />
                              </Tooltip>
                            </TableCell>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableRow>

                  {/* Empty spacing row after row 7 */}
                  {row_index === "7" && (
                    <TableRow style={{ height: "30px" }}>
                      {arrayOfLength19.map((_, index) => (
                        <TableCell
                          key={`empty-col-${index}`}
                          style={{
                            backgroundColor: "transparent",
                            padding: 0,
                            border: "none",
                            width: "20px",
                          }}
                        />
                      ))}
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
};

export default SeatingPlanTableNew;